/**
 * MOSAIC Login Jingle — 6-note ascending chime
 * Uses Web Audio API, zero dependencies, ~800ms total
 * Mute preference stored in localStorage
 */

const JINGLE_MUTE_KEY = "mosaic_jingle_muted";

export function isJingleMuted() {
  return localStorage.getItem(JINGLE_MUTE_KEY) === "true";
}

export function setJingleMuted(muted) {
  localStorage.setItem(JINGLE_MUTE_KEY, muted ? "true" : "false");
}

/**
 * Create an AudioContext during a user gesture (click/submit).
 * Returns the context or null if muted/unsupported.
 */
export function createJingleContext() {
  if (isJingleMuted()) return null;
  try {
    return new (window.AudioContext || window.webkitAudioContext)();
  } catch (e) {
    return null;
  }
}

/**
 * Play the jingle using a pre-created AudioContext.
 * @param {AudioContext} ctx - must be created during a user gesture
 */
export function playJingle(ctx) {
  if (!ctx) return;

  const play = () => {
    const t = ctx.currentTime;
    const notes = [
      { freq: 523.25, start: 0.00, dur: 0.14 },
      { freq: 659.25, start: 0.10, dur: 0.14 },
      { freq: 783.99, start: 0.20, dur: 0.14 },
      { freq: 880.00, start: 0.30, dur: 0.16 },
      { freq: 1046.50, start: 0.42, dur: 0.18 },
      { freq: 1318.51, start: 0.56, dur: 0.35 },
    ];

    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, t + start);
      gain.gain.linearRampToValueAtTime(0.18, t + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t + start);
      osc.stop(t + start + dur + 0.05);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = "triangle";
      osc2.frequency.value = freq * 2;
      gain2.gain.setValueAtTime(0, t + start);
      gain2.gain.linearRampToValueAtTime(0.04, t + start + 0.02);
      gain2.gain.exponentialRampToValueAtTime(0.001, t + start + dur * 0.7);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(t + start);
      osc2.stop(t + start + dur + 0.05);
    });

    setTimeout(() => ctx.close(), 1500);
  };

  if (ctx.state === "suspended") {
    ctx.resume().then(play);
  } else {
    play();
  }
}
